import React, { useState } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Tag,
    Progress,
    Alert,
    Button,
    Space,
    Spin,
    Empty,
    Tooltip,
    Typography,
    Tabs,
    Badge,
    Modal,
    InputNumber,
    Input,
    message,
    Popconfirm
} from 'antd';
import {
    DatabaseOutlined,
    ReloadOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    GlobalOutlined,
    UserOutlined,
    ExclamationCircleOutlined,
    ApiOutlined,
    DeleteOutlined,
    StopOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@/components/common/ThemeProvider';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface PoolStats {
    poolName: string;
    error?: string;
    maxPoolSize: number;
    minIdle: number;
    activeConnections: number;
    idleConnections: number;
    totalConnections: number;
    threadsAwaitingConnection: number;
    connectionTimeout: number;
    idleTimeout: number;
    maxLifetime: number;
    leakDetectionThreshold: number;
    validationTimeout: number;
    utilizationPercent: number;
    healthStatus: string;
    potentialConnectionLeak: boolean;
}

interface ActiveConnection {
    sessionId: number;
    loginName: string;
    hostName: string;
    clientIpAddress: string;
    programName: string;
    status: string;
    loginTime: string;
    lastRequestStartTime: string;
    lastRequestEndTime: string;
    idleSeconds: number;
    command: string;
    waitType: string;
    waitTimeMs: number;
    blockingSessionId: number | null;
    databaseName: string;
    currentQuery: string;
}

interface PotentialLeak {
    sessionId: number;
    loginName: string;
    hostName: string;
    clientIpAddress: string;
    programName: string;
    status: string;
    loginTime: string;
    lastRequestTime: string;
    idleTimeMs: number;
    idleTimeFormatted: string;
    databaseName: string;
    leakThresholdMs: number;
    exceedsThresholdBy: number;
}

interface DatabaseStats {
    topWaitStats: Array<{
        waitType: string;
        waitingTasksCount: number;
        waitTimeMs: number;
        maxWaitTimeMs: number;
        signalWaitTimeMs: number;
    }>;
    connectionsByDatabase: Array<{
        databaseName: string;
        connectionCount: number;
    }>;
}

const useHikariPoolStats = () => {
    return useQuery({
        queryKey: ['/api/service-metrics/hikari/pool-stats'],
        queryFn: async () => {
            const response = await fetch('/api/service-metrics/hikari/pool-stats');
            if (!response.ok) throw new Error('Failed to fetch pool stats');
            return response.json() as Promise<PoolStats>;
        },
        refetchInterval: 10000, // Refresh every 10 seconds
    });
};

const useActiveConnections = () => {
    return useQuery({
        queryKey: ['/api/service-metrics/hikari/active-connections'],
        queryFn: async () => {
            const response = await fetch('/api/service-metrics/hikari/active-connections');
            if (!response.ok) throw new Error('Failed to fetch active connections');
            return response.json() as Promise<ActiveConnection[]>;
        },
        refetchInterval: 15000,
    });
};

const usePotentialLeaks = () => {
    return useQuery({
        queryKey: ['/api/service-metrics/hikari/potential-leaks'],
        queryFn: async () => {
            const response = await fetch('/api/service-metrics/hikari/potential-leaks');
            if (!response.ok) throw new Error('Failed to fetch potential leaks');
            return response.json() as Promise<PotentialLeak[]>;
        },
        refetchInterval: 30000,
    });
};

const useDatabaseStats = () => {
    return useQuery({
        queryKey: ['/api/service-metrics/hikari/database-stats'],
        queryFn: async () => {
            const response = await fetch('/api/service-metrics/hikari/database-stats');
            if (!response.ok) throw new Error('Failed to fetch database stats');
            return response.json() as Promise<DatabaseStats>;
        },
        refetchInterval: 30000,
    });
};

const HikariPoolDashboard: React.FC = () => {
    const { dark } = useTheme();
    const [activeTab, setActiveTab] = useState('overview');
    const [bulkKillModalVisible, setBulkKillModalVisible] = useState(false);
    const [bulkKillMinutes, setBulkKillMinutes] = useState(10);
    const [bulkKillIpAddress, setBulkKillIpAddress] = useState('');
    const [bulkKillDatabase, setBulkKillDatabase] = useState('');
    const [selectedConnectionKeys, setSelectedConnectionKeys] = useState<React.Key[]>([]);
    const [selectedLeakKeys, setSelectedLeakKeys] = useState<React.Key[]>([]);
    const [connectionManagementAvailable, setConnectionManagementAvailable] = useState(true);
    const queryClient = useQueryClient();

    const { data: poolStats, isLoading: statsLoading, refetch: refetchStats } = useHikariPoolStats();
    const { data: activeConnections, isLoading: connectionsLoading, refetch: refetchConnections } = useActiveConnections();
    const { data: potentialLeaks, isLoading: leaksLoading, refetch: refetchLeaks } = usePotentialLeaks();
    const { data: databaseStats, isLoading: dbStatsLoading, refetch: refetchDbStats } = useDatabaseStats();

    const killSessionMutation = useMutation({
        mutationFn: async (sessionId: number) => {
            const response = await fetch(`/api/service-metrics/hikari/kill-session/${sessionId}`, {
                method: 'POST',
            });
            if (!response.ok) throw new Error('Failed to kill session');
            return response.json();
        },
        onSuccess: (data) => {
            if (data.success) {
                message.success(`Session ${data.sessionId} killed successfully`);
                queryClient.invalidateQueries({ queryKey: ['/api/service-metrics/hikari/active-connections'] });
                queryClient.invalidateQueries({ queryKey: ['/api/service-metrics/hikari/potential-leaks'] });
            } else {
                message.error(data.message);
            }
        },
        onError: (error) => {
            message.error('Failed to kill session: ' + error.message);
        },
    });

    const bulkKillMutation = useMutation({
        mutationFn: async (params: { idleMinutes: number; ipAddress?: string; databaseName?: string }) => {
            const queryParams = new URLSearchParams();
            queryParams.append('idleMinutes', params.idleMinutes.toString());
            if (params.ipAddress) queryParams.append('ipAddress', params.ipAddress);
            if (params.databaseName) queryParams.append('databaseName', params.databaseName);

            const response = await fetch(`/api/service-metrics/hikari/kill-idle-sessions?${queryParams.toString()}`, {
                method: 'POST',
            });
            if (!response.ok) throw new Error('Failed to kill idle sessions');
            return response.json();
        },
        onSuccess: (data) => {
            if (data.success) {
                message.success(data.message);
                if (data.errors && data.errors.length > 0) {
                    data.errors.forEach((error: string) => message.warning(error));
                }
            } else {
                message.error(data.message);
            }
            setBulkKillModalVisible(false);
            queryClient.invalidateQueries({ queryKey: ['/api/service-metrics/hikari/active-connections'] });
            queryClient.invalidateQueries({ queryKey: ['/api/service-metrics/hikari/potential-leaks'] });
        },
        onError: (error) => {
            message.error('Failed to kill idle sessions: ' + error.message);
        },
    });

    const killSelectedMutation = useMutation({
        mutationFn: async (sessionIds: number[]) => {
            const response = await fetch('/api/service-metrics/hikari/kill-sessions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sessionIds),
            });
            if (!response.ok) throw new Error('Failed to kill selected sessions');
            return response.json();
        },
        onSuccess: (data) => {
            if (data.success) {
                message.success(data.message);
                if (data.errors && data.errors.length > 0) {
                    data.errors.forEach((error: string) => message.warning(error));
                }
            } else {
                message.error(data.message);
            }
            setSelectedConnectionKeys([]);
            setSelectedLeakKeys([]);
            queryClient.invalidateQueries({ queryKey: ['/api/service-metrics/hikari/active-connections'] });
            queryClient.invalidateQueries({ queryKey: ['/api/service-metrics/hikari/potential-leaks'] });
        },
        onError: (error) => {
            message.error('Failed to kill selected sessions: ' + error.message);
        },
    });

    // Check if connection management is available
    React.useEffect(() => {
        fetch('/api/service-metrics/hikari/available')
            .then(res => res.json())
            .then(available => setConnectionManagementAvailable(available))
            .catch(() => setConnectionManagementAvailable(false));
    }, []);

    const handleRefresh = () => {
        refetchStats();
        refetchConnections();
        refetchLeaks();
        refetchDbStats();
    };

    const getHealthColor = (status: string) => {
        switch (status) {
            case 'HEALTHY': return 'success';
            case 'WARNING': return 'warning';
            case 'CRITICAL': return 'error';
            default: return 'default';
        }
    };

    const getHealthIcon = (status: string) => {
        switch (status) {
            case 'HEALTHY': return <CheckCircleOutlined />;
            case 'WARNING': return <ExclamationCircleOutlined />;
            case 'CRITICAL': return <CloseCircleOutlined />;
            default: return <ClockCircleOutlined />;
        }
    };

    const activeConnectionsColumns = [
        {
            title: 'Session ID',
            dataIndex: 'sessionId',
            key: 'sessionId',
            width: 80,
        },
        {
            title: 'User',
            dataIndex: 'loginName',
            key: 'loginName',
            render: (text: string) => (
                <Space>
                    <UserOutlined />
                    {text}
                </Space>
            ),
        },
        {
            title: 'IP Address',
            dataIndex: 'clientIpAddress',
            key: 'clientIpAddress',
            render: (text: string) => (
                <Space>
                    <GlobalOutlined />
                    <code>{text}</code>
                </Space>
            ),
        },
        {
            title: 'Host',
            dataIndex: 'hostName',
            key: 'hostName',
            ellipsis: true,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'running' ? 'processing' : status === 'sleeping' ? 'default' : 'warning'}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Idle (s)',
            dataIndex: 'idleSeconds',
            key: 'idleSeconds',
            render: (seconds: number) => (
                <Tag color={seconds > 300 ? 'warning' : 'default'}>{seconds}s</Tag>
            ),
        },
        {
            title: 'Database',
            dataIndex: 'databaseName',
            key: 'databaseName',
        },
        {
            title: 'Command',
            dataIndex: 'command',
            key: 'command',
            render: (cmd: string) => cmd || '-',
        },
        {
            title: 'Blocking',
            dataIndex: 'blockingSessionId',
            key: 'blockingSessionId',
            render: (id: number | null) => id ? <Tag color="error">Blocked by {id}</Tag> : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_: any, record: ActiveConnection) => (
                <Popconfirm
                    title="Kill Session"
                    description={`Are you sure you want to kill session ${record.sessionId}?`}
                    onConfirm={() => killSessionMutation.mutate(record.sessionId)}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                >
                    <Button 
                        type="link" 
                        danger 
                        size="small" 
                        icon={<StopOutlined />}
                        loading={killSessionMutation.isPending}
                    >
                        Kill
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    const potentialLeaksColumns = [
        {
            title: 'Session ID',
            dataIndex: 'sessionId',
            key: 'sessionId',
            width: 80,
        },
        {
            title: 'User',
            dataIndex: 'loginName',
            key: 'loginName',
        },
        {
            title: 'IP Address',
            dataIndex: 'clientIpAddress',
            key: 'clientIpAddress',
            render: (text: string) => <code>{text}</code>,
        },
        {
            title: 'Host',
            dataIndex: 'hostName',
            key: 'hostName',
        },
        {
            title: 'Idle Time',
            dataIndex: 'idleTimeFormatted',
            key: 'idleTimeFormatted',
            render: (time: string) => <Tag color="error">{time}</Tag>,
        },
        {
            title: 'Exceeds Threshold By',
            dataIndex: 'exceedsThresholdBy',
            key: 'exceedsThresholdBy',
            render: (ms: number) => <Text type="danger">{Math.round(ms / 1000)}s</Text>,
        },
        {
            title: 'Database',
            dataIndex: 'databaseName',
            key: 'databaseName',
        },
        {
            title: 'Program',
            dataIndex: 'programName',
            key: 'programName',
            ellipsis: true,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_: any, record: PotentialLeak) => (
                <Popconfirm
                    title="Kill Session"
                    description={`Kill session ${record.sessionId} that has been idle for ${record.idleTimeFormatted}?`}
                    onConfirm={() => killSessionMutation.mutate(record.sessionId)}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                >
                    <Button 
                        type="link" 
                        danger 
                        size="small" 
                        icon={<StopOutlined />}
                        loading={killSessionMutation.isPending}
                    >
                        Kill
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    const waitStatsColumns = [
        {
            title: 'Wait Type',
            dataIndex: 'waitType',
            key: 'waitType',
        },
        {
            title: 'Waiting Tasks',
            dataIndex: 'waitingTasksCount',
            key: 'waitingTasksCount',
            render: (count: number) => count.toLocaleString(),
        },
        {
            title: 'Total Wait (ms)',
            dataIndex: 'waitTimeMs',
            key: 'waitTimeMs',
            render: (ms: number) => ms.toLocaleString(),
        },
        {
            title: 'Max Wait (ms)',
            dataIndex: 'maxWaitTimeMs',
            key: 'maxWaitTimeMs',
            render: (ms: number) => ms.toLocaleString(),
        },
    ];

    if (statsLoading) {
        return (
            <div className="flex items-center justify-center h-60">
                <Spin size="large" tip="Loading HikariCP metrics..." />
            </div>
        );
    }

    if (poolStats?.error) {
        return (
            <Alert
                message="HikariCP Metrics Unavailable"
                description={poolStats.error}
                type="warning"
                showIcon
            />
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <Space>
                    <DatabaseOutlined className="text-2xl text-blue-500" />
                    <Title level={4} style={{ margin: 0 }}>
                        HikariCP Connection Pool: {poolStats?.poolName}
                    </Title>
                    {poolStats && (
                        <Tag color={getHealthColor(poolStats.healthStatus)} icon={getHealthIcon(poolStats.healthStatus)}>
                            {poolStats.healthStatus}
                        </Tag>
                    )}
                </Space>
                <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                    Refresh
                </Button>
            </div>

            {/* Alerts */}
            {poolStats?.potentialConnectionLeak && (
                <Alert
                    message="Potential Connection Leak Detected"
                    description="Pool utilization is above 80% with leak detection enabled. Check the Potential Leaks tab for details."
                    type="error"
                    showIcon
                    icon={<WarningOutlined />}
                />
            )}

            {poolStats?.threadsAwaitingConnection && poolStats.threadsAwaitingConnection > 0 && (
                <Alert
                    message={`${poolStats.threadsAwaitingConnection} threads waiting for connection`}
                    description="Consider increasing pool size or investigating slow queries."
                    type="warning"
                    showIcon
                />
            )}

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab={<span><DatabaseOutlined /> Overview</span>} key="overview">
                    {/* Pool Statistics */}
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="Active Connections"
                                    value={poolStats?.activeConnections || 0}
                                    suffix={`/ ${poolStats?.maxPoolSize || 0}`}
                                    valueStyle={{ color: (poolStats?.activeConnections || 0) > (poolStats?.maxPoolSize || 1) * 0.8 ? '#cf1322' : '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="Idle Connections"
                                    value={poolStats?.idleConnections || 0}
                                    suffix={`/ ${poolStats?.minIdle || 0} min`}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="Total Connections"
                                    value={poolStats?.totalConnections || 0}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="Threads Waiting"
                                    value={poolStats?.threadsAwaitingConnection || 0}
                                    valueStyle={{ color: (poolStats?.threadsAwaitingConnection || 0) > 0 ? '#cf1322' : '#3f8600' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Utilization */}
                    <Card className="mt-4" title="Pool Utilization">
                        <Progress
                            percent={poolStats?.utilizationPercent || 0}
                            status={
                                (poolStats?.utilizationPercent || 0) >= 90 ? 'exception' :
                                (poolStats?.utilizationPercent || 0) >= 70 ? 'active' : 'success'
                            }
                            strokeWidth={20}
                            format={(percent) => `${percent}% (${poolStats?.activeConnections}/${poolStats?.maxPoolSize})`}
                        />
                    </Card>

                    {/* Configuration */}
                    <Card className="mt-4" title="Pool Configuration">
                        <Row gutter={[16, 16]}>
                            <Col xs={12} md={6}>
                                <Statistic title="Connection Timeout" value={poolStats?.connectionTimeout || 0} suffix="ms" />
                            </Col>
                            <Col xs={12} md={6}>
                                <Statistic title="Idle Timeout" value={poolStats?.idleTimeout || 0} suffix="ms" />
                            </Col>
                            <Col xs={12} md={6}>
                                <Statistic title="Max Lifetime" value={poolStats?.maxLifetime || 0} suffix="ms" />
                            </Col>
                            <Col xs={12} md={6}>
                                <Statistic 
                                    title="Leak Detection Threshold" 
                                    value={poolStats?.leakDetectionThreshold || 0} 
                                    suffix="ms"
                                    valueStyle={{ color: (poolStats?.leakDetectionThreshold || 0) > 0 ? '#3f8600' : '#faad14' }}
                                />
                            </Col>
                        </Row>
                    </Card>

                    {/* Connections by Database */}
                    {databaseStats?.connectionsByDatabase && databaseStats.connectionsByDatabase.length > 0 && (
                        <Card className="mt-4" title="Connections by Database">
                            <Row gutter={[16, 16]}>
                                {databaseStats.connectionsByDatabase.map((db) => (
                                    <Col xs={12} md={6} key={db.databaseName}>
                                        <Statistic 
                                            title={db.databaseName || 'Unknown'} 
                                            value={db.connectionCount} 
                                            prefix={<DatabaseOutlined />}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    )}
                </TabPane>

                <TabPane 
                    tab={
                        <span>
                            <ApiOutlined /> Active Connections 
                            <Badge count={activeConnections?.length || 0} style={{ marginLeft: 8 }} />
                        </span>
                    } 
                    key="connections"
                >
                    {connectionManagementAvailable && selectedConnectionKeys.length > 0 && (
                        <div className="mb-4">
                            <Space>
                                <Text strong>{selectedConnectionKeys.length} session(s) selected</Text>
                                <Popconfirm
                                    title="Kill Selected Sessions"
                                    description={`Are you sure you want to kill ${selectedConnectionKeys.length} selected session(s)?`}
                                    onConfirm={() => killSelectedMutation.mutate(selectedConnectionKeys.map(k => Number(k)))}
                                    okText="Yes"
                                    cancelText="No"
                                    okButtonProps={{ danger: true }}
                                >
                                    <Button 
                                        type="primary" 
                                        danger 
                                        icon={<DeleteOutlined />}
                                        loading={killSelectedMutation.isPending}
                                    >
                                        Kill Selected
                                    </Button>
                                </Popconfirm>
                                <Button onClick={() => setSelectedConnectionKeys([])}>Clear Selection</Button>
                            </Space>
                        </div>
                    )}
                    <Table
                        columns={activeConnectionsColumns}
                        dataSource={activeConnections || []}
                        loading={connectionsLoading}
                        rowKey="sessionId"
                        pagination={{ pageSize: 20 }}
                        size="small"
                        scroll={{ x: 1200 }}
                        rowSelection={connectionManagementAvailable ? {
                            selectedRowKeys: selectedConnectionKeys,
                            onChange: setSelectedConnectionKeys,
                            selections: [
                                Table.SELECTION_ALL,
                                Table.SELECTION_INVERT,
                                Table.SELECTION_NONE,
                            ],
                        } : undefined}
                    />
                </TabPane>

                <TabPane 
                    tab={
                        <span>
                            <WarningOutlined className={potentialLeaks && potentialLeaks.length > 0 ? 'text-red-500' : ''} /> 
                            Potential Leaks
                            {potentialLeaks && potentialLeaks.length > 0 && (
                                <Badge count={potentialLeaks.length} style={{ marginLeft: 8, backgroundColor: '#f5222d' }} />
                            )}
                        </span>
                    } 
                    key="leaks"
                >
                    <div className="mb-4">
                        <Space>
                            <Button 
                                type="primary" 
                                danger 
                                icon={<DeleteOutlined />}
                                onClick={() => setBulkKillModalVisible(true)}
                                disabled={!connectionManagementAvailable}
                            >
                                Bulk Kill Idle Sessions
                            </Button>
                            {connectionManagementAvailable && selectedLeakKeys.length > 0 && (
                                <>
                                    <Text strong>{selectedLeakKeys.length} leak(s) selected</Text>
                                    <Popconfirm
                                        title="Kill Selected Leaking Sessions"
                                        description={`Are you sure you want to kill ${selectedLeakKeys.length} selected leaking session(s)?`}
                                        onConfirm={() => killSelectedMutation.mutate(selectedLeakKeys.map(k => Number(k)))}
                                        okText="Yes"
                                        cancelText="No"
                                        okButtonProps={{ danger: true }}
                                    >
                                        <Button 
                                            type="primary" 
                                            danger 
                                            icon={<StopOutlined />}
                                            loading={killSelectedMutation.isPending}
                                        >
                                            Kill Selected
                                        </Button>
                                    </Popconfirm>
                                    <Button onClick={() => setSelectedLeakKeys([])}>Clear Selection</Button>
                                </>
                            )}
                        </Space>
                    </div>
                    {potentialLeaks && potentialLeaks.length > 0 ? (
                        <>
                            <Alert
                                message={`${potentialLeaks.length} potential connection leak(s) detected`}
                                description="These connections have been idle longer than the configured leak detection threshold."
                                type="error"
                                showIcon
                                className="mb-4"
                            />
                            <Table
                                columns={potentialLeaksColumns}
                                dataSource={potentialLeaks}
                                loading={leaksLoading}
                                rowKey="sessionId"
                                pagination={{ pageSize: 20 }}
                                size="small"
                                rowSelection={connectionManagementAvailable ? {
                                    selectedRowKeys: selectedLeakKeys,
                                    onChange: setSelectedLeakKeys,
                                    selections: [
                                        Table.SELECTION_ALL,
                                        Table.SELECTION_INVERT,
                                        Table.SELECTION_NONE,
                                    ],
                                } : undefined}
                            />
                        </>
                    ) : (
                        <Empty
                            description="No potential connection leaks detected"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    )}
                </TabPane>

                <TabPane tab={<span><ClockCircleOutlined /> Wait Statistics</span>} key="waits">
                    {databaseStats?.topWaitStats && databaseStats.topWaitStats.length > 0 ? (
                        <Table
                            columns={waitStatsColumns}
                            dataSource={databaseStats.topWaitStats}
                            loading={dbStatsLoading}
                            rowKey="waitType"
                            pagination={false}
                            size="small"
                        />
                    ) : (
                        <Empty
                            description="No wait statistics available"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    )}
                </TabPane>
            </Tabs>

            {/* Bulk Kill Modal */}
            <Modal
                title="Bulk Kill Idle Sessions"
                open={bulkKillModalVisible}
                onCancel={() => setBulkKillModalVisible(false)}
                onOk={() => {
                    bulkKillMutation.mutate({
                        idleMinutes: bulkKillMinutes,
                        ipAddress: bulkKillIpAddress || undefined,
                        databaseName: bulkKillDatabase || undefined,
                    });
                }}
                confirmLoading={bulkKillMutation.isPending}
                okText="Kill Sessions"
                okButtonProps={{ danger: true }}
                width={600}
            >
                <div className="space-y-4">
                    <Alert
                        message="Warning"
                        description="This will terminate all database sessions matching the criteria. Use with caution!"
                        type="warning"
                        showIcon
                    />
                    
                    <div>
                        <label className="block mb-2 font-medium">
                            Idle Time Threshold (minutes)
                        </label>
                        <InputNumber
                            min={1}
                            max={1440}
                            value={bulkKillMinutes}
                            onChange={(value) => setBulkKillMinutes(value || 10)}
                            style={{ width: '100%' }}
                            placeholder="Default: 10 minutes"
                        />
                        <Text type="secondary" className="block mt-1">
                            Kill sessions idle for more than this many minutes
                        </Text>
                    </div>

                    <div>
                        <label className="block mb-2 font-medium">
                            IP Address (Optional)
                        </label>
                        <Input
                            value={bulkKillIpAddress}
                            onChange={(e) => setBulkKillIpAddress(e.target.value)}
                            placeholder="e.g., 192.168.1.100"
                            prefix={<GlobalOutlined />}
                        />
                        <Text type="secondary" className="block mt-1">
                            Only kill sessions from this IP address (leave empty for all)
                        </Text>
                    </div>

                    <div>
                        <label className="block mb-2 font-medium">
                            Database Name (Optional)
                        </label>
                        <Input
                            value={bulkKillDatabase}
                            onChange={(e) => setBulkKillDatabase(e.target.value)}
                            placeholder="e.g., ScaleFiDB"
                            prefix={<DatabaseOutlined />}
                        />
                        <Text type="secondary" className="block mt-1">
                            Only kill sessions connected to this database (leave empty for all)
                        </Text>
                    </div>

                    <Alert
                        message="Example: Legacy Application Cleanup"
                        description={`To close connections from your legacy application that have been idle for more than ${bulkKillMinutes} minutes, enter the legacy application's IP address above.`}
                        type="info"
                        showIcon
                    />
                </div>
            </Modal>
        </div>
    );
};

export default HikariPoolDashboard;
